import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { I18nProvider } from "@/i18n/I18nProvider";
import { Nav } from "@/components/festival/Nav";
import { Footer } from "@/components/festival/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { z } from "zod";
import { getPublicSponsors, type PublicSponsor } from "@/lib/sponsors.functions";
import { SponsorLogoCard } from "@/components/festival/SponsorLogoCard";

export const Route = createFileRoute("/partners")({
  head: () => ({
    meta: [
      { title: "Onze partners — Openluchtfilmfestival Apeldoorn" },
      { name: "description", content: "Onze partners maken het Openluchtfilmfestival mogelijk. Word ook partner van negen zomeravonden openluchtbioscoop." },
      { property: "og:title", content: "Onze partners — Openluchtfilmfestival Apeldoorn" },
      { property: "og:description", content: "Word partner van het Openluchtfilmfestival in Stadspark Berg & Bos." },
    ],
  }),
  component: PartnersPage,
});

type Sponsor = PublicSponsor;

function TierSection({
  title,
  sponsors,
  size,
  cols,
}: {
  title: string;
  sponsors: Sponsor[];
  size: "sm" | "md" | "lg";
  cols: string;
}) {
  if (sponsors.length === 0) return null;
  return (
    <div className="mt-10 first:mt-0">
      <h2 className="font-mono-cinema text-[11px] uppercase tracking-[0.3em] text-gold">{title}</h2>
      <div className={`mt-4 grid gap-4 md:gap-5 ${cols}`}>
        {sponsors.map((s) => (
          <SponsorLogoCard key={s.id} sponsor={s} size={size} href={s.website_url ?? undefined} />
        ))}
      </div>
    </div>
  );
}

function PartnerGrid() {
  const [sponsors, setSponsors] = useState<Sponsor[] | null>(null);

  useEffect(() => {
    getPublicSponsors()
      .then((data) => setSponsors(data.filter((s) => s.logo_url)))
      .catch(() => setSponsors([]));
  }, []);

  if (sponsors === null) {
    return <div className="mt-6 h-32 animate-pulse rounded-2xl bg-night-deep/40" />;
  }

  if (sponsors.length === 0) {
    return (
      <div className="mt-6 rounded-2xl border border-dashed border-gold/30 bg-night-deep/30 p-10 text-center">
        <p className="font-display text-xl text-foreground/85">Onze partners komen binnenkort ✨</p>
        <p className="mt-2 text-sm text-foreground/60">We werken hard aan een mooi rijtje partners voor deze editie.</p>
      </div>
    );
  }

  const byTier = (tier: Sponsor["tier"]) => sponsors.filter((s) => s.tier === tier);

  return (
    <div className="mt-2">
      <TierSection
        title="Hoofdpartners"
        sponsors={byTier("hoofdpartner")}
        size="md"
        cols="grid-cols-2 sm:grid-cols-3 md:grid-cols-3"
      />
      <TierSection
        title="Premium Partners"
        sponsors={byTier("premium")}
        size="md"
        cols="grid-cols-2 sm:grid-cols-3 md:grid-cols-3"
      />
      <TierSection
        title="Partners"
        sponsors={byTier("partner")}
        size="md"
        cols="grid-cols-2 sm:grid-cols-3 md:grid-cols-4"
      />
      <TierSection
        title="Supporters"
        sponsors={byTier("supporter")}
        size="md"
        cols="grid-cols-2 sm:grid-cols-3 md:grid-cols-4"
      />
      <TierSection
        title="Vrienden"
        sponsors={byTier("vriend")}
        size="md"
        cols="grid-cols-2 sm:grid-cols-3 md:grid-cols-4"
      />
    </div>
  );
}

const partnerSchema = z.object({
  company: z.string().trim().min(1, "Vul je bedrijfsnaam in").max(200),
  email: z.string().trim().email("Vul een geldig e-mailadres in").max(320),
  contact_person: z.string().trim().min(1, "Vul de contactpersoon in").max(200),
  budget: z.enum(["500_1k", "1k_3k", "3k_plus"]),
});

function PartnerForm({ onDone }: { onDone: () => void }) {
  const [submitting, setSubmitting] = useState(false);
  const [budget, setBudget] = useState<"500_1k" | "1k_3k" | "3k_plus">("500_1k");

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const parsed = partnerSchema.safeParse({
      company: String(fd.get("company") ?? ""),
      email: String(fd.get("email") ?? ""),
      contact_person: String(fd.get("contact_person") ?? ""),
      budget,
    });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? "Controleer het formulier");
      return;
    }
    setSubmitting(true);
    const { error } = await (supabase as any)
      .from("sponsor_applications")
      .insert(parsed.data);
    setSubmitting(false);
    if (error) {
      toast.error("Er ging iets mis. Probeer het opnieuw.");
      return;
    }
    toast.success("Bedankt! We nemen contact met je op.");
    (e.target as HTMLFormElement).reset();
    onDone();
  }

  return (
    <form onSubmit={onSubmit} className="mt-6 grid gap-5">
      <div className="grid gap-5 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="company" className="text-xs uppercase tracking-widest text-foreground/75">Bedrijfsnaam</Label>
          <Input id="company" name="company" required className="h-12 border-border bg-night-deep/60" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email" className="text-xs uppercase tracking-widest text-foreground/75">E-mailadres</Label>
          <Input id="email" name="email" type="email" required className="h-12 border-border bg-night-deep/60" />
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="contact_person" className="text-xs uppercase tracking-widest text-foreground/75">Contactpersoon</Label>
          <Input id="contact_person" name="contact_person" required className="h-12 border-border bg-night-deep/60" />
        </div>
      </div>

      <div className="space-y-3">
        <p className="text-xs uppercase tracking-widest text-foreground/75">Budget</p>
        <RadioGroup
          value={budget}
          onValueChange={(v) => setBudget(v as "500_1k" | "1k_3k" | "3k_plus")}
          className="grid gap-3 md:grid-cols-3"
        >
          {[
            { v: "500_1k", label: "€ 500 — € 1.000" },
            { v: "1k_3k", label: "€ 1.000 — € 3.000" },
            { v: "3k_plus", label: "€ 3.000 +" },
          ].map((b) => (
            <label key={b.v} className="flex cursor-pointer items-center gap-3 rounded-xl border border-border bg-night-deep/40 p-4 hover:border-gold/50">
              <RadioGroupItem value={b.v} />
              <span className="text-sm">{b.label}</span>
            </label>
          ))}
        </RadioGroup>
      </div>

      <Button type="submit" variant="hero" size="xl" disabled={submitting} className="w-full sm:w-auto">
        {submitting ? "Een moment…" : "Verstuur aanvraag"}
      </Button>
    </form>
  );
}

function PartnersPage() {
  const [showForm, setShowForm] = useState(false);

  return (
    <I18nProvider lang="nl">
      <div className="min-h-screen bg-background">
        <Nav />
        <main className="pt-28 pb-24 md:pt-36">
          <section className="mx-auto max-w-7xl px-5 md:px-8">
            <div className="mb-12 max-w-2xl">
              <p className="font-mono-cinema text-[10px] text-gold">— Partners</p>
              <h1 className="mt-3 font-display text-4xl font-medium leading-[1.05] md:text-6xl">
                Onze <span className="italic text-gold">partners</span>
              </h1>
              <p className="mt-5 text-foreground/75">
                Het Openluchtfilmfestival bestaat dankzij de steun van onze partners.
                Hieronder zie je de bedrijven die deze editie mogelijk maken.
              </p>
            </div>

            <div className="rounded-3xl border border-gold/15 bg-night/60 p-6 md:p-10">
              <PartnerGrid />
            </div>

            <div className="mt-20">
              <h2 className="font-display text-2xl font-medium md:text-3xl">Word ook onze partner</h2>
              <div className="mt-6 rounded-3xl border border-gold/30 bg-gradient-to-br from-night to-night-deep p-6 shadow-soft md:p-10">
                <p className="max-w-2xl text-foreground/75">
                  Verbind je merk aan negen zomeravonden openluchtbioscoop in Stadspark Berg &amp; Bos.
                  Laat je gegevens achter en we nemen contact op met de mogelijkheden.
                </p>
                {!showForm ? (
                  <Button onClick={() => setShowForm(true)} variant="hero" size="xl" className="mt-6">
                    Word partner
                  </Button>
                ) : (
                  <PartnerForm onDone={() => setShowForm(false)} />
                )}
              </div>
            </div>
          </section>
        </main>
        <Footer />
        <Toaster position="top-center" />
      </div>
    </I18nProvider>
  );
}